import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './UploadPage.css';

// Laravel APIに接続（認証後）
const LARAVEL_API_URL = window.location.origin + '/auth-api';

// axiosのデフォルト設定（セッションベース認証用）
axios.defaults.withCredentials = true;

function UploadPage() {
  const navigate = useNavigate();
  const [dataType, setDataType] = useState('file');
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [url, setUrl] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [documents, setDocuments] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCommon, setIsCommon] = useState(false);

  useEffect(() => {
    loadUser();
    loadDocuments();
  }, []);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${LARAVEL_API_URL}/user`);
      console.log('ユーザー情報:', response.data);
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error('ユーザー情報の取得エラー:', error);
      console.error('エラー詳細:', error.response);
      // 認証エラーの場合、チェックボックスを表示しない（一般ユーザー扱い）
      setIsAdmin(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${LARAVEL_API_URL}/documents`);
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('ドキュメント一覧の読み込みエラー:', error);
      if (error.response?.status === 401) {
        // 未認証の場合はログイン画面へ
        window.location.href = '/auth/login';
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    // 画像ファイルの場合、プレビューを表示
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setImagePreview(null);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage('ファイルを選択してください');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('data_type', 'file');
    formData.append('title', title);
    formData.append('comment', comment);
    if (isCommon) {
      formData.append('is_common', '1');
    }

    try {
      const response = await axios.post(`${LARAVEL_API_URL}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage(response.data.message);
      setFile(null);
      setTitle('');
      setComment('');
      setImagePreview(null);
      setIsCommon(false);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleWebUpload = async (e) => {
    e.preventDefault();
    if (!url) {
      setMessage('URLを入力してください');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const response = await axios.post(`${LARAVEL_API_URL}/documents`, {
        data_type: 'web',
        url,
        title,
        comment,
        is_common: isCommon ? 1 : 0
      });
      setMessage(response.data.message);
      setUrl('');
      setTitle('');
      setComment('');
      setIsCommon(false);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleTextUpload = async (e) => {
    e.preventDefault();
    if (!question || !answer) {
      setMessage('質問と回答の両方を入力してください');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const response = await axios.post(`${LARAVEL_API_URL}/documents`, {
        data_type: 'text',
        title,
        question,
        answer,
        is_common: isCommon ? 1 : 0
      });
      setMessage(response.data.message);
      setTitle('');
      setQuestion('');
      setAnswer('');
      setIsCommon(false);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('このドキュメントを削除しますか？')) return;

    try {
      await axios.delete(`${LARAVEL_API_URL}/documents/${docId}`);
      setMessage('ドキュメントを削除しました');
      loadDocuments();
    } catch (error) {
      setMessage('削除に失敗しました');
    }
  };

  const handleToggleCommon = async (docId, isCurrentlyCommon) => {
    const confirmMsg = isCurrentlyCommon
      ? 'このドキュメントを個人知識に変更しますか？'
      : 'このドキュメントを共通知識に変更しますか？（全ユーザーが閲覧可能になります）';

    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await axios.put(`${LARAVEL_API_URL}/documents/${docId}/toggle-common`);
      setMessage(response.data.message);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || '変更に失敗しました');
    }
  };

  return (
    <div className="upload-page">
      <div className="upload-container">
        <header className="upload-header">
          <button className="home-btn" onClick={() => navigate('/')}>
            🏠
          </button>
          <h1>BOT Learning</h1>
          {isAdmin && (
            <span className="admin-badge">
              👑 管理者
            </span>
          )}
        </header>

        {message && (
          <div className={`message-alert ${message.includes('失敗') || message.includes('エラー') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div className="upload-form-box">
          <div className="data-type-selector">
            <label>
              <input
                type="radio"
                value="file"
                checked={dataType === 'file'}
                onChange={(e) => setDataType(e.target.value)}
              />
              ファイルデータ
            </label>
            <label>
              <input
                type="radio"
                value="web"
                checked={dataType === 'web'}
                onChange={(e) => setDataType(e.target.value)}
              />
              Webデータ
            </label>
            <label>
              <input
                type="radio"
                value="text"
                checked={dataType === 'text'}
                onChange={(e) => setDataType(e.target.value)}
              />
              テキストデータ
            </label>
          </div>

          {dataType === 'file' && (
            <form onSubmit={handleFileUpload} className="upload-form">
              <div className="form-group">
                <label>ファイル選択 (PDF, Word, Excel, PowerPoint, TXT, 画像)</label>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.txt,.png,.jpg,.jpeg,.gif,.bmp,.webp"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </div>
              {imagePreview && (
                <div className="image-preview-container">
                  <label>画像プレビュー</label>
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                </div>
              )}
              <div className="form-group">
                <label>タイトル</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                  placeholder={file && file.type.startsWith('image/') ? '画像のタイトルを入力' : ''}
                />
              </div>
              <div className="form-group">
                <label>コメント</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="3"
                  disabled={uploading}
                  placeholder={file && file.type.startsWith('image/') ? 'この画像について説明を入力してください' : ''}
                />
              </div>
              {isAdmin && (
                <div className="form-group" style={{flexDirection: 'row', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="checkbox"
                    id="file-common"
                    checked={isCommon}
                    onChange={(e) => setIsCommon(e.target.checked)}
                    style={{width: '18px', height: '18px', cursor: 'pointer'}}
                  />
                  <label htmlFor="file-common" style={{margin: 0, cursor: 'pointer', fontWeight: 500}}>
                    共通知識として保存（全ユーザーが利用可能）
                  </label>
                </div>
              )}
              <button type="submit" disabled={uploading || !file}>
                {uploading ? 'アップロード中...' : '送信'}
              </button>
            </form>
          )}

          {dataType === 'web' && (
            <form onSubmit={handleWebUpload} className="upload-form">
              <div className="form-group">
                <label>URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>タイトル</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>コメント</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="3"
                  disabled={uploading}
                />
              </div>
              {isAdmin && (
                <div className="form-group" style={{flexDirection: 'row', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="checkbox"
                    id="web-common"
                    checked={isCommon}
                    onChange={(e) => setIsCommon(e.target.checked)}
                    style={{width: '18px', height: '18px', cursor: 'pointer'}}
                  />
                  <label htmlFor="web-common" style={{margin: 0, cursor: 'pointer', fontWeight: 500}}>
                    共通知識として保存（全ユーザーが利用可能）
                  </label>
                </div>
              )}
              <button type="submit" disabled={uploading || !url}>
                {uploading ? 'アップロード中...' : '送信'}
              </button>
            </form>
          )}

          {dataType === 'text' && (
            <form onSubmit={handleTextUpload} className="upload-form">
              <div className="form-group">
                <label>タイトル</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>質問</label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows="3"
                  disabled={uploading}
                />
              </div>
              <div className="form-group">
                <label>回答</label>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows="3"
                  disabled={uploading}
                />
              </div>
              {isAdmin && (
                <div className="form-group" style={{flexDirection: 'row', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="checkbox"
                    id="text-common"
                    checked={isCommon}
                    onChange={(e) => setIsCommon(e.target.checked)}
                    style={{width: '18px', height: '18px', cursor: 'pointer'}}
                  />
                  <label htmlFor="text-common" style={{margin: 0, cursor: 'pointer', fontWeight: 500}}>
                    共通知識として保存（全ユーザーが利用可能）
                  </label>
                </div>
              )}
              <button type="submit" disabled={uploading || !question || !answer}>
                {uploading ? 'アップロード中...' : '送信'}
              </button>
            </form>
          )}
        </div>

        <div className="documents-list">
          <h2>個人の知識</h2>
          <div className="documents-grid">
            {documents.filter(doc => !doc.is_common).map((doc) => (
              <div key={doc.id} className="document-card">
                <div className="document-header">
                  <h3>{doc.title}</h3>
                  <span className={`file-type ${doc.file_type}`}>{doc.file_type}</span>
                </div>
                <p className="document-comment">{doc.comment}</p>
                <div className="document-footer">
                  <span>{doc.chunks_count} チャンク</span>
                  <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                  <div style={{display: 'flex', gap: '8px'}}>
                    {isAdmin && (
                      <button
                        className="toggle-btn"
                        onClick={() => handleToggleCommon(doc.id, false)}
                        title="共通知識に変更"
                      >
                        🌐
                      </button>
                    )}
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(doc.id)}
                    >
                      削除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="documents-list" style={{marginTop: '40px'}}>
          <h2>共通知識 <span style={{fontSize: '14px', color: '#9ca3af', fontWeight: 'normal', marginLeft: '8px'}}>全ユーザー共有</span></h2>
          <div className="documents-grid">
            {documents.filter(doc => doc.is_common).map((doc) => (
              <div key={doc.id} className="document-card">
                <div className="document-header">
                  <h3>🌐 {doc.title}</h3>
                  <span className="file-type web">{doc.file_type}</span>
                </div>
                <p className="document-comment">{doc.comment}</p>
                <div className="document-footer">
                  <span>{doc.chunks_count} チャンク</span>
                  <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                  {isAdmin && (
                    <div style={{display: 'flex', gap: '8px'}}>
                      <button
                        className="toggle-btn"
                        onClick={() => handleToggleCommon(doc.id, true)}
                        title="個人知識に変更"
                      >
                        👤
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(doc.id)}
                      >
                        削除
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadPage;
